import { useEffect, useState } from 'react';
import { Configure, connectInfiniteHits, InstantSearch, SearchBox } from 'react-instantsearch-dom';
import { useTranslation } from 'react-i18next';
import { Organization } from '../../models/interfaces/organization';
import { useAuthState } from '../../utilities/contexts/auth-state-context';
import { useSwitchOrganization } from '../../utilities/hooks/use-switch-organization';
import { createLogger } from '../../utilities/logger';
import searchClient, { SearchIndexes } from '../../utilities/search-client';
import OrganizationService from '../../utilities/services/organization-service';
import UserUtil from '../../utilities/user-util';
import { Badge } from '../badge/badge';
import { Loader } from '../loader/loader';
import { Modal } from '../modal/modal';

const COMPONENT_CLASS = "c-switch-organization-hierarchy-modal";
const log = createLogger("SwitchOrganizationHierarchyModal");

type SwitchOrganizationHierarchyModalProps = {
    open: boolean,
    setOpen: (value: boolean | ((prevVar: boolean) => boolean)) => void,
    setIsSwitching: (value: boolean | ((prevVar: boolean) => boolean)) => void
}

export const SwitchOrganizationHierarchyModal = (props: SwitchOrganizationHierarchyModalProps) => {
    const {
        open,
        setOpen,
        setIsSwitching,
    } = props;

    const { t } = useTranslation();
    const { state } = useAuthState();
    const [algoliaFilters, setAlgoliaFilters] = useState<string>();
    const [searchState, setSearchState] = useState({
        sortBy: SearchIndexes.organizationsByNameAsc,
    });
    const [parentOrganization, setParentOrganization] = useState<Organization>();
    const [selectedOrganizationName, setSelectedOrganizationName] = useState(state.organization?.name);
    const {
        selectedOrganizationId,
        setSelectedOrganizationId,
        handleSwitchOrganizationClick,
    } = useSwitchOrganization({ setOpen, setIsSwitching });

    const parentOrganizationId = state.organization?.parentOrganizationId
        ?? state.organization?.id
        ?? state.user?.organizationId;
    const canAccessParentOrganization = parentOrganization != null
        && UserUtil.canAccessOrganization(state.user, parentOrganization.id);
    const subOrganizationCount = parentOrganization == null
        ? undefined
        : UserUtil.isAccountHolder(state.user)
            ? parentOrganization.subOrganizationIds?.length ?? 0
            : (state.user?.accessibleSubOrganizationIds?.length ?? 0)
                + (state.user?.organizationId != null && state.user.organizationId !== parentOrganization.id ? 1 : 0);

    useEffect(() => {
        const setFilters = async () => {
            setAlgoliaFilters(await UserUtil.getAlgoliaAccessibleOrganizationFilter(state.user));
        };

        setFilters();
    }, [state.user])

    useEffect(() => {
        if (!open || parentOrganizationId == null) {
            return;
        }

        const loadParentOrganization = async () => {
            try {
                const organization = await OrganizationService.get(parentOrganizationId);
                setParentOrganization(organization ?? undefined);
            } catch (e) {
                log.error("Failed to load parent organization", {
                    exception: e,
                    organizationId: parentOrganizationId,
                });
            }
        };

        loadParentOrganization();
    }, [open, parentOrganizationId])

    const isCurrentOrganization = (organizationId?: string) => organizationId != null
        && organizationId === state.organization?.id;

    const handleSelectOrganization = (organizationId?: string, organizationName?: string) => {
        if (organizationId == null) {
            return;
        }

        setSelectedOrganizationId(organizationId);
        setSelectedOrganizationName(organizationName);
    }

    const getUsersLabel = (hit: any) => {
        if (hit.userCount == null) {
            return undefined;
        }

        if (hit.userLimit == null) {
            return t('switchOrganizationModal.usersCountNoLimit', { userCount: hit.userCount });
        }

        return t('switchOrganizationModal.usersCount', { userCount: hit.userCount, limit: hit.userLimit });
    }

    const getSubtitle = () => {
        if (parentOrganization == null || subOrganizationCount == null) {
            return undefined;
        }

        if (subOrganizationCount === 0) {
            return t('switchOrganizationModal.subtitleNoSubOrganizations', { name: parentOrganization.name });
        }

        return t('switchOrganizationModal.subtitle', {
            count: subOrganizationCount,
            name: parentOrganization.name,
        });
    }

    const customHits = ({
        hits,
        hasPrevious,
        refinePrevious,
        hasMore,
        refineNext,
    }: any) => {
        const subOrganizations = hits.filter((h: any) => h.id !== parentOrganization?.id);

        return (
            <div className={`${COMPONENT_CLASS}__organization-list__wrapper`}>
                <div className={`${COMPONENT_CLASS}__organization-list__action ${!hasPrevious ? "hidden" : ""}`}>
                    <button
                        type="button"
                        disabled={!hasPrevious}
                        onClick={refinePrevious}>
                        {t('buttons.btn_showPrev')}
                    </button>
                </div>
                <ul className={`${COMPONENT_CLASS}__organization-list`}>
                    {parentOrganization != null &&
                        <li
                            className={`${COMPONENT_CLASS}__organization-list__parent ${canAccessParentOrganization ? "" : "-locked"} ${selectedOrganizationId === parentOrganization.id ? "-selected" : ""}`}
                            onClick={canAccessParentOrganization
                                ? () => handleSelectOrganization(parentOrganization.id, parentOrganization.name)
                                : undefined}>
                            {canAccessParentOrganization &&
                                <span className={`${COMPONENT_CLASS}__organization-list__radio ${selectedOrganizationId === parentOrganization.id ? "-checked" : ""}`} />
                            }
                            <div className={`${COMPONENT_CLASS}__organization-list__item-content`}>
                                <div className={`${COMPONENT_CLASS}__organization-list__item__name`}>
                                    {parentOrganization.name}
                                </div>
                                {!canAccessParentOrganization &&
                                    <div className={`${COMPONENT_CLASS}__organization-list__item__meta`}>
                                        {t('switchOrganizationModal.parentNoAccess')}
                                    </div>
                                }
                            </div>
                            {isCurrentOrganization(parentOrganization.id) &&
                                <Badge text={t('switchOrganizationModal.current')} />
                            }
                        </li>
                    }
                    {subOrganizations.map((h: any, i: number) => (
                        <li
                            className={`${COMPONENT_CLASS}__organization-list__sub ${selectedOrganizationId === h.id ? "-selected" : ""}`}
                            onClick={() => handleSelectOrganization(h.id, h.name)}
                            key={`organization-${i}`}>
                            <span className={`${COMPONENT_CLASS}__organization-list__radio ${selectedOrganizationId === h.id ? "-checked" : ""}`} />
                            <div className={`${COMPONENT_CLASS}__organization-list__item-content`}>
                                <div className={`${COMPONENT_CLASS}__organization-list__item__name`}>
                                    {h.name}
                                </div>
                                {getUsersLabel(h) != null &&
                                    <div className={`${COMPONENT_CLASS}__organization-list__item__meta`}>
                                        {getUsersLabel(h)}
                                    </div>
                                }
                            </div>
                            {isCurrentOrganization(h.id) &&
                                <Badge text={t('switchOrganizationModal.current')} />
                            }
                        </li>
                    ))}
                </ul>
                <div className={`${COMPONENT_CLASS}__organization-list__action ${!hasMore ? "hidden" : ""}`}>
                    <button
                        type="button"
                        disabled={!hasMore}
                        onClick={refineNext}>
                        {t('buttons.btn_showMore')}
                    </button>
                </div>
            </div>
        )
    }

    const CustomHits = connectInfiniteHits(customHits);

    const submitButtonText = selectedOrganizationName != null
        ? t('switchOrganizationModal.switchTo', { name: selectedOrganizationName })
        : t('switchOrganizationModal.switchToThis');
    const subtitle = getSubtitle();

    return (
        <Modal
            isOpen={open}
            isLoading={false}
            componentClass={COMPONENT_CLASS}
            onClose={setOpen}
            title={t('switchOrganizationModal.title')}
            defaultModalActions={true}
            submitButtonText={submitButtonText}
            onSubmit={() => handleSwitchOrganizationClick()}
            onCancel={() => setOpen(false)}
        >
            <div className={`${COMPONENT_CLASS}__body`}>
                {subtitle != null &&
                    <p className={`${COMPONENT_CLASS}__subtitle`}>
                        {subtitle}
                    </p>
                }
                {open &&
                    <InstantSearch
                        indexName={SearchIndexes.organizationsByNameAsc}
                        searchClient={searchClient}
                        searchState={searchState}
                        onSearchStateChange={searchState => {
                            setSearchState(searchState);
                        }}>
                        <Configure
                            filters={algoliaFilters}
                            hitsPerPage={50}
                        />
                        <SearchBox
                            autoFocus
                            className={`${COMPONENT_CLASS}__search`}
                            searchAsYouType={true}
                            showLoadingIndicator={true}
                            loadingIndicator={<Loader isVisible={true} />}
                            translations={{
                                placeholder: t('switchOrganizationModal.searchOrganizations')
                            }}
                        />
                        <CustomHits />
                    </InstantSearch>
                }
            </div>
        </Modal>
    );
}
